import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { configuration } from '../../../configuration';
import { reduxServices } from '../../../reducers/reduxServices';
import { setUserProperties } from '../../../utils/analytics/dataLayer';
import { UNSET_IDENTITY } from '../../../utils/analytics/identity';
import { cancelCsvExport } from '../../../utils/csv/csvExport';
import { notifyEmbedSignOut } from '../../../utils/embedAuth';
import ErrorPage from '../../ErrorPage';
import { AppLoader } from '../../Loader';
import {
  clearActiveCognitoClient,
  getCognitoAccessToken,
  isStaffMode,
  waitForStoredSessionProbe,
} from './configureAmplify';
import MfaSetupGate from './mfa/MfaSetupGate';
import {
  POST_AUTH_EXTERNAL_REDIRECT_KEY,
  POST_AUTH_REDIRECT_KEY,
  type PostAuthDestination,
  clearOAuthFailure,
  clearStaleExternalDestination,
  markDestinationWritten,
  parseDestination,
  peekOAuthFailure,
  reportSignInRedirectFailure,
  toCustomState,
} from './postAuthRedirect';

// Routes (prefix match) that render unauthenticated. Without this, the gate
// kicks off Hosted UI before the invitation / staff handoff pages get to run
// their own signIn() and the user loops.
const UNAUTHENTICATED_PATH_PREFIXES = [
  '/login',
  '/logged-out',
  '/auth/mfa-reset',
  '/invitation',
  '/set-password',
  '/staff/accept-invitation',
];

export function isUnauthenticatedPath(pathname: string): boolean {
  return UNAUTHENTICATED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

const SIGNING_OUT_KEY = 'sr-signing-out';

function getOAuthError(): { description: string; error: string } | null {
  // Amplify reports a failed code exchange on the Hub only -- nothing lands
  // on the URL -- so reading `?error` alone rendered nothing and the gate
  // silently restarted the flow (APP-2364, ADR-CM-037).
  //
  // Read without consuming: this runs in the render body, and the gate
  // re-renders on every Authenticator transition. Consuming here would leave
  // the next render with no error and send it into the redirect branch below.
  const hubFailure = peekOAuthFailure();

  if (hubFailure) {
    return {
      description: hubFailure.message || 'Unknown authentication error',
      error: hubFailure.name || 'signInWithRedirect_failure',
    };
  }

  if (window.location.pathname !== '/login-callback') return null;
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');

  if (!error) return null;

  return {
    description:
      params.get('error_description') || 'Unknown authentication error',
    error,
  };
}

// Whether the active session came from Hosted UI / federated sign-in. Read
// before `signOut` runs -- it clears these keys.
function wasOAuthSignIn(): boolean {
  if (typeof localStorage === 'undefined') return false;
  if (localStorage.getItem('amplify-signin-with-hostedUI') === 'true')
    return true;
  const clientIds = [
    configuration.COGNITO_APP_CLIENT_ID,
    configuration.COGNITO_APP_CLIENT_ID_MSR3_STAFF,
  ].filter(Boolean);

  return clientIds.some((clientId) => {
    const raw = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.oauthSignIn`,
    );

    return raw?.split(',')[0] === 'true';
  });
}

// Hosted UI sign-ins get a Cognito redirect from signOut(); password sign-ins
// don't, so navigate to /logged-out ourselves to clear the gate spinner.
export async function managedLoginSignOut(): Promise<void> {
  sessionStorage.setItem(SIGNING_OUT_KEY, '1');
  // First, and synchronously: this path never reaches `resetAppState`, which
  // cancels for the other two, and nothing below may stop a running export
  // fetching personal data once the member has chosen to sign out.
  cancelCsvExport();
  const oauthRedirectExpected = wasOAuthSignIn();

  // Clear before signOut so the persisted client pin is dropped even if
  // signOut throws or kicks the user out via a Hosted UI redirect.
  clearActiveCognitoClient();
  // Signing out doesn't clear Redux's `userMe` (no reset/setUserMe dispatch
  // happens here), so useAnalyticsIdentity's null-transition never fires on
  // its own -- drop the identity from the dataLayer explicitly before the
  // redirect below, so it doesn't stick to the next session in this tab.
  setUserProperties(UNSET_IDENTITY);
  // Same problem for the embedded MSR2 frame, plus a second one: even if
  // `userMe` did reset, `getCognitoTokens()` already returns `null` once
  // `signOut()` below has run, so there would be no tokens left for a clear
  // to ride on. Tell any live handshake to push `identity_cleared` now,
  // while the session is still valid, rather than leaving MSR2's GTM
  // holding this member's `user_id` for the rest of the tab's life. A failed
  // handshake must not keep the member signed in, so carry on regardless.
  try {
    await notifyEmbedSignOut();
  } catch {
    /* sign out anyway */
  }
  try {
    await signOut({ global: true });
  } catch {
    /* fall through to the manual redirect below */
  }
  if (!oauthRedirectExpected && typeof window !== 'undefined') {
    window.location.replace('/logged-out');
  }
}

/**
 * Top-level auth provider for the Cognito path. Wraps the app in
 * Amplify's Authenticator.Provider so useAuthenticator() works globally.
 *
 * Flow: unauthenticated users are first redirected to the in-app `/login`
 * interstitial (PreLoginPage) which surfaces the security-upgrade notice and
 * help links. From there the user clicks through to the Cognito Managed Login
 * page (signInWithRedirect). After the OAuth code exchange on /login-callback,
 * Amplify picks up the session. Authenticated users pass through MfaSetupGate
 * before reaching the app.
 */
const CognitoManagedLoginProvider = ({ children }: React.PropsWithChildren) => {
  // The Authenticator's Hub listener signs out on a failed refresh, losing
  // `return_to`; reading the session first clears a dead token quietly.
  const [sessionProbed, setSessionProbed] = useState(false);

  useEffect(() => {
    let active = true;

    waitForStoredSessionProbe().then(() => {
      if (active) setSessionProbed(true);
    });

    return () => {
      active = false;
    };
  }, []);

  if (!sessionProbed) {
    return <AppLoader.FlexLoader loaderHeight="h-screen" />;
  }

  return (
    <Authenticator.Provider>
      <ManagedLoginGate>{children}</ManagedLoginGate>
    </Authenticator.Provider>
  );
};

function ManagedLoginGate({ children }: { children?: ReactNode }) {
  const { authStatus } = useAuthenticator();
  const tokenSetterInitialized = useRef(false);
  const redirecting = useRef(false);

  useEffect(() => {
    if (!tokenSetterInitialized.current) {
      tokenSetterInitialized.current = true;
      reduxServices.globalState.getAccessTokenSilently = getCognitoAccessToken;
    }
  }, []);

  useEffect(() => {
    reduxServices.globalState.cognitoSignOut = managedLoginSignOut;
  }, []);

  if (isUnauthenticatedPath(window.location.pathname)) {
    sessionStorage.removeItem(SIGNING_OUT_KEY);

    return <>{children}</>;
  }

  if (authStatus === 'configuring') {
    return <AppLoader.FlexLoader loaderHeight="h-screen" />;
  }

  if (authStatus === 'unauthenticated') {
    if (sessionStorage.getItem(SIGNING_OUT_KEY)) {
      return <AppLoader.FlexLoader loaderHeight="h-screen" />;
    }

    const oauthError = getOAuthError();

    if (oauthError) {
      return (
        <MemoryRouter initialEntries={[window.location.pathname]}>
          <ErrorPage genericError />
        </MemoryRouter>
      );
    }

    if (!redirecting.current) {
      redirecting.current = true;
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      const skipPaths = ['/', '/login-callback', '/logged-out', '/logout'];
      // Same weakness as the interstitial had for cross-app returns: this
      // key is the only record of where the member was going, and any tab
      // discontinuity across the OAuth round-trip loses it silently. The
      // destination now also rides the OAuth `state` (ADR-CM-037); the write
      // stays as the fallback for one release.
      let destination: PostAuthDestination | null = null;

      if (!skipPaths.includes(window.location.pathname)) {
        sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, currentPath);
        destination = parseDestination(currentPath);
      }

      // Skip the pre-login interstitial when SSO is expected to be
      // seamless: cross-app MFA-setup arrivals, MSR2 iframe embeds
      // (/embed/*), and staff arriving from Retool (?sr_staff=1 or
      // sticky pin).
      const skipInterstitial =
        window.location.pathname === '/auth/mfa-setup' ||
        window.location.pathname.startsWith('/embed/') ||
        isStaffMode();

      if (skipInterstitial) {
        // A cross-app MFA-setup arrival carries its own absolute `return_to`,
        // which outranks the same-origin path we are standing on -- that path
        // is `/auth/mfa-setup` itself.
        //
        // Scoped to this branch: the `else` below starts no OAuth flow, so
        // writing the external key there would leave it for whichever sign-in
        // the member completes next and send someone who asked for an MSR3
        // path (`/workspaces/1?return_to=<msr2>`) to MSR2 instead. That path
        // is already carried by POST_AUTH_REDIRECT_KEY above.
        const crossAppReturnTo = parseDestination(
          new URLSearchParams(window.location.search).get('return_to'),
        );

        if (crossAppReturnTo?.kind === 'external') {
          destination = crossAppReturnTo;
          sessionStorage.setItem(
            POST_AUTH_EXTERNAL_REDIRECT_KEY,
            crossAppReturnTo.value,
          );
        } else {
          // Same reasoning as the interstitial: this flow carries no cross-app
          // destination, so a key still present belongs to an earlier attempt
          // that never completed and must not outrank `destination` above.
          clearStaleExternalDestination();
        }

        if (destination) markDestinationWritten(destination);
        // A previous page load's failure must not survive into the flow we
        // are about to start.
        clearOAuthFailure();
        signInWithRedirect(
          destination ? { customState: toCustomState(destination) } : undefined,
        ).catch((err) => {
          reportSignInRedirectFailure(err, { origin: 'ManagedLoginGate' });
          window.location.assign('/login');
        });
      } else {
        window.location.assign('/login');
      }
    }

    return <AppLoader.FlexLoader loaderHeight="h-screen" />;
  }

  return <MfaSetupGate>{children}</MfaSetupGate>;
}

export { POST_AUTH_EXTERNAL_REDIRECT_KEY, POST_AUTH_REDIRECT_KEY };
export default CognitoManagedLoginProvider;
